import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { propertyAPI, reservationAPI, roomAPI } from '@/services/api';
import { Calendar, Search, Filter } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function ReservationsPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
      if (response.data.length > 0) {
        
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [reservationsRes, roomsRes] = await Promise.all([
        reservationAPI.getAll(selectedProperty),
        roomAPI.getAll(selectedProperty)
      ]);
      
      setReservations(reservationsRes.data);
      setRooms(roomsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    }
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.nombre : 'N/A';
  };

  const getStatusBadge = (estado) => {
    const variants = {
      confirmada: 'default',
      pendiente: 'secondary',
      checked_in: 'default',
      checked_out: 'outline',
      cancelada: 'destructive'
    };
    
    const labels = {
      confirmada: 'Confirmada',
      pendiente: 'Pendiente',
      checked_in: 'Check-in',
      checked_out: 'Check-out',
      cancelada: 'Cancelada'
    };

    return (
      <Badge variant={variants[estado] || 'default'} className={`status-${estado}`}>
        {labels[estado] || estado}
      </Badge>
    );
  };

  const filteredReservations = reservations.filter((res) => {
    const matchesSearch = 
      res.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      res.guest_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      res.fuente.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || res.estado === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando reservas...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reservations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}} data-testid="reservations-title">
              Reservas
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestiona todas tus reservas
            </p>
          </div>

          {properties.length > 0 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-full sm:w-[280px]" data-testid="reservations-property-selector">
                <SelectValue placeholder="Selecciona una propiedad" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, email o fuente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="reservations-search-input"
            />
          </div>
          
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="reservations-filter-select">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="confirmada">Confirmada</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="checked_in">Check-in</SelectItem>
              <SelectItem value="checked_out">Check-out</SelectItem>
              <SelectItem value="cancelada">Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Reservations List */}
        <div className="space-y-4">
          {filteredReservations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2" style={{fontFamily: 'Manrope'}}>
                  No hay reservas
                </h3>
                <p className="text-muted-foreground">
                  {searchTerm || filterStatus !== 'all' 
                    ? 'No se encontraron reservas con los filtros aplicados' 
                    : 'Todavía no tienes reservas registradas'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredReservations.map((reservation) => (
              <Card key={reservation.id} className="hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold" style={{fontFamily: 'Manrope'}}>
                          {reservation.guest_name}
                        </h3>
                        {getStatusBadge(reservation.estado)}
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Habitación:</span> {getRoomName(reservation.room_id)}
                        </div>
                        <div>
                          <span className="font-medium">Check-in:</span> {format(parseISO(reservation.fecha_checkin), 'dd MMM yyyy', { locale: es })}
                        </div>
                        <div>
                          <span className="font-medium">Check-out:</span> {format(parseISO(reservation.fecha_checkout), 'dd MMM yyyy', { locale: es })}
                        </div>
                        <div>
                          <span className="font-medium">Fuente:</span> {reservation.fuente}
                        </div>
                        <div>
                          <span className="font-medium">Huéspedes:</span> {reservation.num_huespedes}
                        </div>
                        <div>
                          <span className="font-medium">Total:</span> ${reservation.precio_total.toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 lg:items-end">
                      <div className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
                        ${reservation.precio_total.toLocaleString()}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        ${reservation.precio_por_noche.toLocaleString()} / noche
                      </div>
                    </div>
                  </div>

                  {reservation.notas && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium">Notas:</span> {reservation.notas}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Summary */}
        {filteredReservations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle style={{fontFamily: 'Manrope'}}>Resumen</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total Reservas</p>
                  <p className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
                    {filteredReservations.length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ingresos Totales</p>
                  <p className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
                    ${filteredReservations.reduce((sum, res) => sum + res.precio_total, 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Precio Promedio</p>
                  <p className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
                    ${Math.round(filteredReservations.reduce((sum, res) => sum + res.precio_total, 0) / filteredReservations.length).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
